import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const translations = {
  en: {
    common: {
      login: 'Login',
      register: 'Register',
      logout: 'Logout',
      back: 'Back',
      submit: 'Submit',
      loading: 'Loading...',
      view: 'View',
      viewPdf: 'View PDF',
      create: 'Create',
      cancel: 'Cancel',
      close: 'Close',
      downloadPdf: 'Download Feedback PDF',
      submissions: 'Submissions',
      assignments: 'Assignments',
      classrooms: 'Classrooms',
      classroom: 'Classroom',
      teachers: 'Teachers',
      assistants: 'Assistants',
      users: 'Users',
      files: 'Files',
      title: 'Title',
      description: 'Description',
      adminSecret: 'Admin Secret',
      subscription: 'Subscription',
      status: 'Status',
      months: 'Months',
      startDate: 'Start date',
      endDate: 'End date',
      update: 'Update',
      name: 'Name',
      email: 'Email',
      password: 'Password',
      total: 'Total',
      points: 'Points',
      due: 'Due',
      timeLeft: 'Time left',
      noDeadline: 'No deadline',
      pastDue: 'Past due',
      noAssignmentsFound: 'No assignments found.',
      noSubmissionsFound: 'No submissions found.',
      gradeOnBehalf: 'Grade on behalf',
      selectStudent: 'Select student',
      selectAssignment: 'Select assignment',
      uploadPdf: 'Upload PDF',
      noClassroomsFound: 'No classrooms found.',
      noTeachersFound: 'No teachers found.',
      noUsersFound: 'No users found.',
      remove: 'Remove',
      refresh: 'Refresh',
      actions: 'Actions',
    },
    landing: {
      headline: 'Assignments, Grading, and Feedback Made Easy',
      subhead:
        'Sa7a7ly helps students and teachers manage assignments, submissions, and feedback in one clear workflow.',
      studentSuccess: 'Student Success',
      teacherEfficiency: 'Teacher Efficiency',
      clearFeedback: 'Clear Feedback',
      organize: 'Organize',
      organizeTitle: 'One hub for every class',
      organizeBody: 'Keep assignments, submissions, and feedback in one place.',
      improve: 'Improve',
      improveTitle: 'Faster grading',
      improveBody: 'Automated scoring and feedback help teachers move quickly.',
      support: 'Support',
      supportTitle: 'Clear next steps',
      supportBody: 'Students always know what to do and how to improve.',
      connect: 'Connect',
      connectTitle: 'Teachers and assistants',
      connectBody: 'Invite assistants to support grading and class updates.',
      startToday: 'Start today',
      startBody: 'Join Sa7a7ly to build clear, organized classrooms with faster and clearer grading.',
    },
    footer: {
      brandTitle: 'Smarter Learning',
      brandBody:
        'A focused platform for assignments, feedback, and progress tracking in one place.',
      aboutTitle: 'Who We Are',
      aboutBody:
        'Sa7a7ly is an education web app designed to make classroom workflows clear, fast, and transparent.',
      joinTitle: 'Join Us',
      joinBody:
        'Ready to modernize your classroom? Start with Sa7a7ly and give students clearer next steps.',
      contactTitle: 'Contact',
      whatsapp: 'WhatsApp: 01095490525',
      copyright: 'Sa7a7ly. All rights reserved.',
    },
    auth: {
      welcomeBack: 'Welcome back',
      createAccount: 'Create your account',
      loginTitle: 'Login',
      registerStudent: 'Student Registration',
      registerAssistant: 'Assistant Registration',
      loginHelp: 'Enter your email and password to continue.',
      registerHelpStudent: 'Create your student account to access classrooms.',
      registerHelpAssistant: 'Enter your assistant code to complete setup.',
      dontHave: "Don't have an account?",
      alreadyHave: 'Already have an account?',
      forgotPassword: 'Forgot password?',
      forgotPasswordTitle: 'Forgot your password?',
      forgotPasswordHelp: 'Enter your email address and we will send a password reset link if the account exists.',
      forgotPasswordSuccess: 'A password reset link was sent.',
      noAccountPrompt: "Don't have an account?",
      registerIfNoAccount: "Register if you don't have an account",
      sendResetLink: 'Send Reset Link',
      emailSentTitle: 'Check your email',
      resetPasswordTitle: 'Reset your password',
      resetPasswordHelp: 'Choose a new password for your account.',
      resetPasswordButton: 'Reset Password',
      passwordResetSuccess: 'Your password has been reset successfully.',
      loginAsAssistant: 'Login as Assistant',
      backToStudent: 'Back to Student Registration',
      assistantCode: 'Teacher Assistant Code',
      confirmPassword: 'Confirm Password',
      showPassword: 'Show',
      hidePassword: 'Hide',
      validationTitle: 'Please review your details',
      registrationIssue: 'We could not complete your registration',
      loginIssue: 'We could not sign you in',
      resetIssue: 'We could not reset your password',
    },
    authErrors: {
      nameRequired: 'Name is required.',
      nameMin: 'Name must be at least 2 characters.',
      emailRequired: 'Email is required.',
      emailInvalid: 'Enter a valid email address.',
      passwordRequired: 'Password is required.',
      passwordMin: 'Password must be at least 6 characters.',
      confirmPasswordRequired: 'Please confirm your password.',
      passwordMismatch: 'Passwords do not match.',
      missingFields: 'Please complete the required fields highlighted below.',
      reviewHighlightedFields: 'Please correct the highlighted fields and try again.',
      resetTokenMissing: 'This password reset link is missing or invalid.',
      assistantCodeRequired: 'Assistant code is required.',
      assistantCodeInvalid: 'Assistant code must be exactly 8 letters or numbers.',
      network: 'Unable to reach server. Check your connection and try again.',
      emailNotFound: 'No account was found for this email address.',
      forgotPasswordEmailNotFound:
        'This email is incorrect. Please check it again or register if you do not have an account.',
      passwordIncorrect: 'The password you entered is incorrect. Please check it again or use Forgot password?',
      invalidCredentials: 'Invalid email or password.',
      loginForbidden: 'Your account is not allowed to login right now.',
      tooManyAttempts: 'Too many attempts. Please wait and try again.',
      server: 'Server error. Please try again in a moment.',
      loginFailed: 'Login failed. Please try again.',
      checkAssistantDetails: 'Please check your details and assistant code.',
      checkDetails: 'Please check your details and try again.',
      assistantCodeExpired: 'Assistant code is invalid or expired.',
      notAllowed: 'You are not allowed to register with these details.',
      emailExists: 'This email is already registered. Try logging in instead.',
      assistantRegisterFailed: 'Assistant registration failed. Please try again.',
      registerFailed: 'Registration failed. Please try again.',
    },
    errors: {
      failedLoadClassrooms: 'Failed to load classrooms.',
      failedJoinClassroom: 'Failed to join classroom.',
      failedLoadSubmissions: 'Failed to load submissions.',
      failedSubmitAssignment: 'Failed to submit assignment.',
    },
    admin: {
      panel: 'Admin Panel',
      overviewAssignments: 'Assignments overview',
      overviewSubmissions: 'Submissions overview',
      overviewClassrooms: 'Classrooms overview',
      teacherManagement: 'Teacher management',
      createTeacher: 'Create Teacher',
      assignmentsOverview: 'Monitor all assignments across classrooms and keep quality consistent.',
      submissionsOverview: 'Review submission flow and keep grading visibility across classes.',
      classroomsOverview: 'Track classroom setup and keep join codes visible.',
      teachersOverview: 'Create teacher accounts and track the teaching team in one place.',
    },
    dashboards: {
      student: 'Student Dashboard',
      teacher: 'Teacher Dashboard',
      assistant: 'Assistant Dashboard',
      welcome: 'Welcome',
    },
    teacher: {
      removeAssistantConfirm:
        'Remove this assistant from your team? They will lose access to your classrooms.',
      removeAssistantSuccess: 'Assistant removed successfully.',
      removeAssistantFailed: 'Failed to remove the assistant.',
    },
    classroom: {
      joinAClassroom: 'Join a Classroom',
      joinCode: 'Join Code',
      shareCode: 'Share this code with students so they can join the classroom.',
      createAssignment: 'Create Assignment',
      manageAssignments: 'Manage assignments',
      manageAssignmentsButton: 'Manage Assignments',
      submitAssignment: 'Submit Assignment',
      viewClassroom: 'View Classroom',
      assignmentsTitle: 'Assignments',
      noAssignments: 'No assignments available yet.',
      noAssignmentsTeacher: 'No assignments yet',
      createFirstAssignment: 'Create your first assignment',
      classroomHub: 'Classroom Hub',
      stayOnTop: 'Stay on top of your classes',
      studentHubBody: 'Review tasks, submit your work, and track points in one place.',
      submit: 'Submit',
      inOneClick: 'In one click',
      track: 'Track',
      yourPoints: 'Your points',
      clear: 'Clear',
      nextSteps: 'Next steps',
      checkBackLater: 'Check back later or ask your teacher for updates.',
      classroomTools: 'Classroom tools',
      teacherHubBody: 'Create assignments, share the join code, and keep students on track.',
      share: 'Share',
      quick: 'Quick',
      createTasks: 'Create tasks',
      studentsTitle: 'Classroom Students',
      searchStudents: 'Search students by name or email',
      noStudentsYet: 'No students in this classroom yet.',
      noStudentsMatch: 'No students match your search.',
      removeStudent: 'Remove student',
      removeStudentConfirm:
        'Remove this student from the classroom? They will lose access to this classroom until they join again.',
      removeStudentSuccess: 'Student removed from the classroom.',
      removeStudentFailed: 'Failed to remove the student from the classroom.',
    },
    studentDashboard: {
      today: 'Today',
      upcomingDueDates: 'Upcoming due dates',
      noUpcomingDeadlines: 'No deadlines in the next 7 days.',
      pending: 'Pending',
      assignmentsToSubmit: 'Assignments to submit',
      assignmentSingular: 'Assignment',
      assignmentPlural: 'Assignments',
      pendingLower: 'pending',
      progress: 'Progress',
      yourPerformance: 'Your performance',
      quickJoin: 'Quick Join',
      askTeacher: 'Ask your teacher',
      joinHelp: 'Enter the class code to access assignments and feedback.',
      enterJoinCode: 'Enter join code',
      myClasses: 'My Classes',
      all: 'All',
      active: 'Active',
      archived: 'Archived',
      searchClasses: 'Search classes',
      classCardSubtitle: 'Assignments, submissions, and updates.',
      feedback: 'Feedback',
      recentFeedback: 'Recent feedback',
      submitToGetFeedback: 'Submit assignments to receive feedback.',
      notAvailable: 'N/A',
      help: 'Help',
      howItWorks: 'How it works',
      step1: '1. Join your classroom with the code from your teacher.',
      step2: '2. Submit your assignment PDF before the deadline.',
      step3: '3. Review feedback and improve your next submission.',
    },
    submit: {
      submitTitle: 'Submit Assignment',
      resultTitle: 'Submission Result',
      submitted: 'Assignment submitted!',
      issueTitle: 'We could not submit your assignment',
      uploadHint: 'Upload your assignment as a PDF file. If something goes wrong, check the message below and try again.',
      fileReadyTitle: 'File ready to submit',
      processingBadge: 'Submission received',
      processingTitle: 'We are grading your assignment',
      processingTitleAfterReview: 'Your assignment is being prepared for review',
      processingSuccess: 'Your assignment was received successfully.',
      processingBody:
        'You can stay on this page or leave it. Grading will continue in the background and your result will appear once it is ready.',
      processingBodyAfterReview:
        'Teacher or assistant review is in progress. You can leave this page and come back later to check the result.',
      processingStatus: 'In progress',
      processingJustNow: 'Just now',
      processingVisibilityAfterReview: 'After review',
      processingVisibilityAfterDeadline: 'After deadline',
      gradedByAi: 'Your submission has been graded by AI.',
      resultHiddenUntilDeadline:
        'Your assignment is submitted. Results will be visible after the deadline.',
      resultHiddenUntilReview:
        'Your assignment is submitted. Results will be visible after teacher/assistant review.',
      waitingReviewTitle: 'Submitted Successfully',
      waitingReviewBody:
        'Your work is waiting for teacher/assistant review. You will see your grade and feedback once review is published.',
      waitingReviewHint: 'You can return to dashboard and check back later.',
      feedback: 'Feedback',
      grade: 'Grade',
      requestResubmit: 'Request Resubmission',
      requestReasonPlaceholder: 'Explain why you need to resubmit...',
      resubmitPending: 'Resubmission request pending approval.',
      resubmitDeclined: 'Resubmission request declined.',
      resubmitApproved: 'Resubmission approved. You can submit a new version.',
      submitNewVersion: 'Submit New Version',
      closed: 'Submission is closed. The due date has passed.',
    },
    submitErrors: {
      pdfRequired: 'Please upload your assignment PDF before submitting.',
      pdfOnly: 'Only PDF files are accepted for assignment submission.',
      pdfTooLarge: 'This PDF is too large. Please upload a file smaller than 10 MB and try again.',
      uploadInvalid: 'We could not read that PDF file. Please choose another PDF and try again.',
      assignmentNotFound: 'This assignment could not be found. Refresh the page and try again.',
      alreadySubmitted:
        'You already submitted this assignment. If you need another attempt, request a resubmission from your teacher or assistant.',
      closed: 'This assignment is closed because the due date has passed.',
      notAllowed: 'You are not allowed to submit this assignment right now.',
      network: 'We could not reach the server. Check your connection and try again.',
      server: 'Something went wrong on our side. Please try again in a moment.',
      submitFailed: 'We could not submit your assignment. Please try again.',
      requestReasonRequired: 'Please explain why you need to resubmit before sending the request.',
      requestFailed: 'We could not send your resubmission request. Please try again.',
      lastSubmissionFailed:
        'Your last submission could not be processed. Please review your PDF and try submitting again.',
    },
    resubmissions: {
      title: 'Resubmission requests',
      review: 'Review queue',
      approve: 'Approve',
      decline: 'Decline',
      reason: 'Reason',
      noRequests: 'No resubmission requests right now.',
    },
    createAssignment: {
      title: 'Create Assignment',
      issueTitle: 'We could not create the assignment',
      titlePlaceholder: 'Assignment title',
      descriptionPlaceholder: 'Assignment description',
      descriptionHelp: 'Add clear instructions to help students complete the task.',
      totalPoints: 'Total Points',
      modelAnswer: 'Model Answer PDF',
      dueDate: 'Due Date (Server Time)',
      dueDateHelp: 'Students will see the deadline and time left.',
      resultVisibility: 'Result Visibility',
      visibilityImmediate: 'Show right after submission',
      visibilityAfterDeadline: 'Show after deadline',
      visibilityAfterReview: 'Show after teacher/assistant review',
      resultVisibilityHelp:
        'Choose when students can view grades and feedback for this assignment.',
      gradingProfile: 'Grading Type',
      gradingGeneral: 'General Subjects',
      gradingArabicEssay: 'Arabic Essay',
      gradingProfileHelp:
        'Choose the grading logic used for this assignment.',
    },
    createAssignmentErrors: {
      titleRequired: 'Assignment title is required.',
      totalPointsRequired: 'Total points is required.',
      totalPointsInvalid: 'Enter a valid total points value greater than zero.',
      dueDateRequired: 'A due date is required when results are shown after the deadline.',
      modelAnswerRequired: 'Please upload the model answer PDF.',
      createFailed: 'Failed to create assignment. Please try again.',
    },
    createClassroom: {
      title: 'Create Classroom',
      name: 'Classroom Name',
      namePlaceholder: 'e.g., Physics 101',
      nameHelp: 'Keep the name short and clear for students.',
    },
    gradeOnBehalf: {
      assignmentFallback: 'Assignment',
      studentFallback: 'Student',
      heroBody: 'Submit and grade student work on behalf of learners in this classroom.',
      savedFeedbacks: 'Saved Feedbacks',
      submissionForm: 'Submission Form',
      pdfOnly: 'PDF files only',
      studentName: 'Student Name',
      pdfFile: 'PDF File',
      submissionReceived: 'Submission Received',
      aiResult: 'AI Grading Result',
      processedSuccessfully: 'Your on-behalf submission was processed successfully.',
      latestSubmissions: 'Latest on-behalf submissions',
      noSavedFeedbacks: 'No saved grade-on-behalf feedbacks yet.',
      notAvailable: 'N/A',
    },
    progress: {
      title: 'Student Progress',
      subtitle: 'Progress',
      headline: 'Track your improvements',
      average: 'Average',
      best: 'Best',
      latest: 'Latest',
      count: 'Submissions',
      trend: 'Grade trend',
      trendHelp: 'Your grades over time based on AI feedback.',
      advice: 'Advice',
      noData: 'No data yet. Submit assignments to see progress.',
      excellent: 'Excellent work. Keep the same pace and polish your answers.',
      good: 'Good progress. Focus on the weak points to go higher.',
      needsWork: 'Your grades are low. Review feedback and ask for help when needed.',
      keepGoing: 'Keep going. Submit more assignments to build your progress chart.',
    },
    subscription: {
      trial: 'Trial',
      active: 'Active',
      pastDue: 'Past Due',
      canceled: 'Canceled',
      expired: 'Subscription expired',
      inactive: 'Subscription inactive',
      manage: 'Manage subscriptions',
      trialCta: 'You are on a trial. Subscribe to keep access after it ends.',
      inactiveCta: 'Your subscription is inactive. Please renew to continue.',
      activeCta: 'Your subscription is active.',
    },
  },
  ar: {
    common: {
      login: 'تسجيل الدخول',
      register: 'إنشاء حساب',
      logout: 'تسجيل خروج',
      back: 'رجوع',
      submit: 'تسليم',
      loading: 'جاري التحميل...',
      view: 'عرض',
      viewPdf: 'عرض ملف PDF',
      create: 'إنشاء',
      cancel: 'إلغاء',
      close: 'إغلاق',
      downloadPdf: 'تحميل تقرير التقييم PDF',
      submissions: 'التسليمات',
      assignments: 'الواجبات',
      classrooms: 'الفصول',
      classroom: 'الفصل',
      teachers: 'المدرسين',
      assistants: 'المساعدين',
      users: 'المستخدمين',
      files: 'الملفات',
      title: 'العنوان',
      description: 'الوصف',
      adminSecret: 'كلمة سر الأدمن',
      subscription: 'الاشتراك',
      status: 'الحالة',
      months: 'الشهور',
      startDate: 'تاريخ البداية',
      endDate: 'تاريخ النهاية',
      update: 'تحديث',
      name: 'الاسم',
      email: 'الإيميل',
      password: 'كلمة المرور',
      total: 'الإجمالي',
      points: 'الدرجات',
      due: 'موعد التسليم',
      timeLeft: 'الوقت المتبقي',
      noDeadline: 'بدون موعد',
      pastDue: 'متأخر',
      noAssignmentsFound: 'لا توجد واجبات.',
      noSubmissionsFound: 'لا توجد تسليمات.',
      gradeOnBehalf: 'تصحيح بالنيابة',
      selectStudent: 'اختار طالب',
      selectAssignment: 'اختار واجب',
      uploadPdf: 'رفع PDF',
      noClassroomsFound: 'لا توجد فصول.',
      noTeachersFound: 'لا يوجد مدرسين.',
      noUsersFound: 'لا يوجد مستخدمين.',
      remove: 'إزالة',
      refresh: 'تحديث',
      actions: 'إجراءات',
    },
    landing: {
      headline: 'الواجبات بشكل أبسط',
      subhead:
        'صححلي تساعد الطلبة والمدرسين في تنظيم الواجبات والتسليمات والملاحظات في خطوات واضحة.',
      studentSuccess: 'نجاح الطلبة',
      teacherEfficiency: 'كفاءة المدرسين',
      clearFeedback: 'ملاحظات واضحة',
      organize: 'تنظيم',
      organizeTitle: 'مكان واحد لكل فصل',
      organizeBody: 'اجمع الواجبات والتسليمات والملاحظات في مكان واحد.',
      improve: 'تحسين',
      improveTitle: 'تصحيح أسرع',
      improveBody: 'درجات وملاحظات آلية تساعد المدرسين ينجزوا بسرعة.',
      support: 'دعم',
      supportTitle: 'خطوات واضحة',
      supportBody: 'الطالب يعرف يعمل إيه وكيف يتحسّن.',
      connect: 'تواصل',
      connectTitle: 'المدرسين والمساعدين',
      connectBody: 'ضيف مساعدين لدعم التصحيح وتحديثات الفصل.',
      startToday: 'ابدأ النهارده',
      startBody: 'انضم لصححلي لتنظيم الفصل وتقديم تصحيح أسرع وملاحظات أوضح.',
    },
    footer: {
      brandTitle: 'تعلّم أذكى',
      brandBody:
        'منصة مركزة لإدارة الواجبات والملاحظات ومتابعة التقدم في مكان واحد.',
      aboutTitle: 'من نحن',
      aboutBody:
        'صححلي منصة تعليمية مصممة لتجعل إدارة الفصل أوضح وأسرع وأكثر شفافية.',
      joinTitle: 'انضم لنا',
      joinBody:
        'جاهز لتطوير الفصل؟ ابدأ مع صححلي وامنح طلابك ملاحظات أوضح وخطوات عملية.',
      contactTitle: 'تواصل معنا',
      whatsapp: 'واتساب: 01095490525',
      copyright: 'صححلي. جميع الحقوق محفوظة.',
    },
    auth: {
      welcomeBack: 'أهلًا بعودتك',
      createAccount: 'اعمل حسابك',
      loginTitle: 'تسجيل الدخول',
      registerStudent: 'تسجيل طالب',
      registerAssistant: 'تسجيل مساعد',
      loginHelp: 'اكتب الإيميل وكلمة المرور للمتابعة.',
      registerHelpStudent: 'اعمل حساب الطالب عشان تدخل الفصول.',
      registerHelpAssistant: 'اكتب كود المساعد لإكمال التسجيل.',
      dontHave: 'ما عندكش حساب؟',
      alreadyHave: 'عندك حساب بالفعل؟',
      forgotPassword: 'نسيت كلمة المرور؟',
      forgotPasswordTitle: 'هل نسيت كلمة المرور؟',
      forgotPasswordHelp: 'اكتب بريدك الإلكتروني وسنرسل رابط إعادة تعيين كلمة المرور إذا كان الحساب موجوداً.',
      forgotPasswordSuccess: 'تم إرسال رابط إعادة تعيين كلمة المرور.',
      noAccountPrompt: 'ليس لديك حساب؟',
      registerIfNoAccount: 'سجل إذا لم يكن لديك حساب',
      sendResetLink: 'إرسال رابط إعادة التعيين',
      emailSentTitle: 'تحقق من بريدك الإلكتروني',
      resetPasswordTitle: 'إعادة تعيين كلمة المرور',
      resetPasswordHelp: 'اختر كلمة مرور جديدة لحسابك.',
      resetPasswordButton: 'إعادة تعيين كلمة المرور',
      passwordResetSuccess: 'تمت إعادة تعيين كلمة المرور بنجاح.',
      loginAsAssistant: 'تسجيل كمساعد',
      backToStudent: 'رجوع لتسجيل الطالب',
      assistantCode: 'كود مساعد المدرس',
      confirmPassword: 'تأكيد كلمة المرور',
      showPassword: 'إظهار',
      hidePassword: 'إخفاء',
      validationTitle: 'راجع البيانات من فضلك',
      registrationIssue: 'لم نتمكن من إكمال التسجيل',
      loginIssue: 'لم نتمكن من تسجيل دخولك',
      resetIssue: 'لم نتمكن من إعادة تعيين كلمة المرور',
    },
    authErrors: {
      nameRequired: 'الاسم مطلوب.',
      nameMin: 'الاسم لازم يكون حرفين على الأقل.',
      emailRequired: 'الإيميل مطلوب.',
      emailInvalid: 'اكتب إيميل صحيح.',
      passwordRequired: 'كلمة المرور مطلوبة.',
      passwordMin: 'كلمة المرور لازم تكون 6 حروف على الأقل.',
      confirmPasswordRequired: 'من فضلك أكد كلمة المرور.',
      passwordMismatch: 'كلمتا المرور غير متطابقتين.',
      missingFields: 'من فضلك أكمل الحقول المطلوبة المظللة بالأسفل.',
      reviewHighlightedFields: 'من فضلك صحح الحقول المظللة ثم حاول مرة أخرى.',
      resetTokenMissing: 'رابط إعادة تعيين كلمة المرور غير صالح أو مفقود.',
      assistantCodeRequired: 'كود المساعد مطلوب.',
      assistantCodeInvalid: 'كود المساعد لازم يكون 8 حروف أو أرقام بالظبط.',
      network: 'مش قادرين نوصل للسيرفر. اتأكد من النت وحاول تاني.',
      emailNotFound: 'لا يوجد حساب مرتبط بعنوان البريد الإلكتروني هذا.',
      forgotPasswordEmailNotFound:
        'هذا البريد الإلكتروني غير صحيح. راجعه مرة أخرى أو سجل إذا لم يكن لديك حساب.',
      passwordIncorrect: 'كلمة المرور التي أدخلتها غير صحيحة. راجعها مرة أخرى أو استخدم نسيت كلمة المرور؟',
      invalidCredentials: 'الإيميل أو كلمة المرور غير صحيح.',
      loginForbidden: 'الحساب غير مسموح له بتسجيل الدخول حالياً.',
      tooManyAttempts: 'محاولات كتير. استنى شوية وحاول تاني.',
      server: 'في مشكلة في السيرفر. حاول تاني بعد شوية.',
      loginFailed: 'فشل تسجيل الدخول. حاول تاني.',
      checkAssistantDetails: 'راجع البيانات وكود المساعد.',
      checkDetails: 'راجع البيانات وحاول تاني.',
      assistantCodeExpired: 'كود المساعد غير صحيح أو منتهي.',
      notAllowed: 'غير مسموح بالتسجيل بهذه البيانات.',
      emailExists: 'الإيميل ده مسجل بالفعل. جرّب تسجيل الدخول.',
      assistantRegisterFailed: 'فشل تسجيل المساعد. حاول تاني.',
      registerFailed: 'فشل إنشاء الحساب. حاول تاني.',
    },
    errors: {
      failedLoadClassrooms: 'فشل تحميل الفصول.',
      failedJoinClassroom: 'فشل الانضمام للفصل.',
      failedLoadSubmissions: 'فشل تحميل التسليمات.',
      failedSubmitAssignment: 'فشل تسليم الواجب.',
    },
    admin: {
      panel: 'لوحة الإدارة',
      overviewAssignments: 'نظرة عامة على الواجبات',
      overviewSubmissions: 'نظرة عامة على التسليمات',
      overviewClassrooms: 'نظرة عامة على الفصول',
      teacherManagement: 'إدارة المدرسين',
      createTeacher: 'إنشاء مدرس',
      assignmentsOverview: 'تابع كل الواجبات على كل الفصول.',
      submissionsOverview: 'تابع عملية التسليم والدرجات في كل الفصول.',
      classroomsOverview: 'تابع إعداد الفصول وأكواد الانضمام.',
      teachersOverview: 'أنشئ حسابات المدرسين وادِر الفريق.',
    },
    dashboards: {
      student: 'لوحة الطالب',
      teacher: 'لوحة المدرس',
      assistant: 'لوحة المساعد',
      welcome: 'أهلًا',
    },
    teacher: {
      removeAssistantConfirm:
        'هل تريد إزالة هذا المساعد من فريقك؟ سيفقد الوصول إلى فصولك.',
      removeAssistantSuccess: 'تمت إزالة المساعد بنجاح.',
      removeAssistantFailed: 'فشل إزالة المساعد.',
    },
    classroom: {
      joinAClassroom: 'انضم لفصل',
      joinCode: 'كود الانضمام',
      shareCode: 'شارك الكود مع الطلبة للانضمام للفصل.',
      createAssignment: 'إنشاء تكليف',
      manageAssignments: 'إدارة الواجبات',
      manageAssignmentsButton: 'إدارة الواجبات',
      submitAssignment: 'تسليم الواجب',
      viewClassroom: 'عرض الفصل',
      assignmentsTitle: 'الواجبات',
      noAssignments: 'لا توجد واجبات حالياً.',
      noAssignmentsTeacher: 'لا توجد واجبات بعد',
      createFirstAssignment: 'أنشئ أول واجب',
      classroomHub: 'مركز الفصل',
      stayOnTop: 'تابع فصولك بسهولة',
      studentHubBody: 'راجع المهام، سلّم شغلك، وتابع درجاتك في مكان واحد.',
      submit: 'سلّم',
      inOneClick: 'بضغطة واحدة',
      track: 'تابع',
      yourPoints: 'درجاتك',
      clear: 'واضح',
      nextSteps: 'الخطوات الجاية',
      checkBackLater: 'ارجع لاحقًا أو اسأل المدرس عن التحديثات.',
      classroomTools: 'أدوات الفصل',
      teacherHubBody: 'أنشئ واجبات، شارك كود الانضمام، وخلي الطلاب ماشيين صح.',
      share: 'شارك',
      quick: 'سريع',
      createTasks: 'إنشاء مهام',
      studentsTitle: 'طلاب الفصل',
      searchStudents: 'ابحث عن طالب بالاسم أو الإيميل',
      noStudentsYet: 'لا يوجد طلاب في هذا الفصل حتى الآن.',
      noStudentsMatch: 'لا يوجد طلاب مطابقون لبحثك.',
      removeStudent: 'إزالة الطالب',
      removeStudentConfirm:
        'هل تريد إزالة هذا الطالب من الفصل؟ سيفقد الوصول إلى هذا الفصل حتى ينضم مرة أخرى.',
      removeStudentSuccess: 'تمت إزالة الطالب من الفصل.',
      removeStudentFailed: 'فشل إزالة الطالب من الفصل.',
    },
    studentDashboard: {
      today: 'اليوم',
      upcomingDueDates: 'المواعيد القريبة',
      noUpcomingDeadlines: 'لا توجد مواعيد خلال 7 أيام.',
      pending: 'مطلوب',
      assignmentsToSubmit: 'واجبات للتسليم',
      assignmentSingular: 'واجب',
      assignmentPlural: 'واجبات',
      pendingLower: 'مطلوبة',
      progress: 'التقدم',
      yourPerformance: 'مستواك',
      quickJoin: 'انضم بسرعة',
      askTeacher: 'اسأل المدرس',
      joinHelp: 'اكتب كود الفصل علشان توصل للواجبات والملاحظات.',
      enterJoinCode: 'اكتب كود الانضمام',
      myClasses: 'فصولي',
      all: 'الكل',
      active: 'نشط',
      archived: 'مؤرشف',
      searchClasses: 'ابحث عن فصل',
      classCardSubtitle: 'واجبات، تسليمات، وتحديثات.',
      feedback: 'الملاحظات',
      recentFeedback: 'أحدث الملاحظات',
      submitToGetFeedback: 'سلّم واجبات علشان توصلك الملاحظات.',
      notAvailable: 'غير متاح',
      help: 'مساعدة',
      howItWorks: 'إزاي تستخدمه',
      step1: '1. انضم للفصل بكود من المدرس.',
      step2: '2. سلّم الواجب PDF قبل الموعد.',
      step3: '3. راجع الملاحظات وحسّن التسليم الجاي.',
    },
    submit: {
      submitTitle: 'تسليم الواجب',
      resultTitle: 'نتيجة التسليم',
      submitted: 'تم تسليم الواجب!',
      issueTitle: 'لم نتمكن من تسليم الواجب',
      uploadHint: 'ارفع الواجب كملف PDF. إذا ظهرت مشكلة، راجع الرسالة بالأسفل ثم حاول مرة أخرى.',
      fileReadyTitle: 'الملف جاهز للتسليم',
      processingBadge: 'تم استلام التسليم',
      processingTitle: 'نحن نصحح واجبك الآن',
      processingTitleAfterReview: 'واجبك الآن قيد التجهيز للمراجعة',
      processingSuccess: 'تم استلام واجبك بنجاح.',
      processingBody:
        'يمكنك البقاء في هذه الصفحة أو مغادرتها. سيستمر التصحيح في الخلفية وستظهر النتيجة عندما تصبح جاهزة.',
      processingBodyAfterReview:
        'مراجعة المدرس أو المساعد جارية الآن. يمكنك مغادرة الصفحة والعودة لاحقًا للاطلاع على النتيجة.',
      processingStatus: 'قيد التنفيذ',
      processingJustNow: 'الآن',
      processingVisibilityAfterReview: 'بعد المراجعة',
      processingVisibilityAfterDeadline: 'بعد الموعد النهائي',
      gradedByAi: 'تم تصحيح التسليم بالذكاء الاصطناعي.',
      resultHiddenUntilDeadline:
        'تم استلام الواجب. ستظهر النتيجة بعد انتهاء موعد التسليم.',
      resultHiddenUntilReview:
        'تم استلام الواجب. ستظهر النتيجة بعد مراجعة المدرس أو المساعد.',
      waitingReviewTitle: 'تم التسليم بنجاح',
      waitingReviewBody:
        'تكليفك الآن في انتظار مراجعة المدرس أو المساعد. ستظهر الدرجة والملاحظات بعد نشر المراجعة.',
      waitingReviewHint: 'يمكنك الرجوع للوحة المتابعة والعودة لاحقًا.',
      feedback: 'الملاحظات',
      grade: 'الدرجة',
      requestResubmit: 'طلب إعادة تسليم',
      requestReasonPlaceholder: 'اكتب سبب طلب إعادة التسليم...',
      resubmitPending: 'طلب إعادة التسليم قيد المراجعة.',
      resubmitDeclined: 'تم رفض طلب إعادة التسليم.',
      resubmitApproved: 'تم قبول إعادة التسليم. يمكنك إرسال نسخة جديدة.',
      submitNewVersion: 'تسليم نسخة جديدة',
      closed: 'التسليم مقفول. الموعد انتهى.',
    },
    submitErrors: {
      pdfRequired: 'من فضلك ارفع ملف الواجب PDF قبل التسليم.',
      pdfOnly: 'يُسمح فقط بملفات PDF عند تسليم الواجب.',
      pdfTooLarge: 'ملف الـ PDF كبير جدًا. من فضلك ارفع ملفًا أقل من 10 ميجابايت ثم حاول مرة أخرى.',
      uploadInvalid: 'لم نتمكن من قراءة ملف الـ PDF هذا. اختر ملفًا آخر ثم حاول مرة أخرى.',
      assignmentNotFound: 'لم نتمكن من العثور على هذا الواجب. حدّث الصفحة ثم حاول مرة أخرى.',
      alreadySubmitted:
        'لقد قمت بتسليم هذا الواجب بالفعل. إذا كنت تحتاج محاولة أخرى، اطلب إعادة التسليم من المدرس أو المساعد.',
      closed: 'هذا الواجب مغلق لأن موعد التسليم انتهى.',
      notAllowed: 'غير مسموح لك بتسليم هذا الواجب الآن.',
      network: 'تعذر الوصول إلى السيرفر. تأكد من الاتصال بالإنترنت ثم حاول مرة أخرى.',
      server: 'حدثت مشكلة من جهتنا. حاول مرة أخرى بعد قليل.',
      submitFailed: 'لم نتمكن من تسليم الواجب. حاول مرة أخرى.',
      requestReasonRequired: 'من فضلك اكتب سبب طلب إعادة التسليم قبل إرسال الطلب.',
      requestFailed: 'لم نتمكن من إرسال طلب إعادة التسليم. حاول مرة أخرى.',
      lastSubmissionFailed:
        'تعذر معالجة آخر تسليم لك. راجع ملف الـ PDF ثم حاول التسليم مرة أخرى.',
    },
    resubmissions: {
      title: 'طلبات إعادة التسليم',
      review: 'قائمة المراجعة',
      approve: 'قبول',
      decline: 'رفض',
      reason: 'السبب',
      noRequests: 'لا يوجد طلبات إعادة تسليم الآن.',
    },
    createAssignment: {
      title: 'إنشاء واجب',
      issueTitle: 'لم نتمكن من إنشاء الواجب',
      titlePlaceholder: 'عنوان الواجب',
      descriptionPlaceholder: 'وصف الواجب',
      descriptionHelp: 'اكتب تعليمات واضحة تساعد الطلبة.',
      totalPoints: 'إجمالي الدرجات',
      modelAnswer: 'ملف إجابة نموذجية PDF',
      dueDate: 'موعد التسليم (بتوقيت السيرفر)',
      dueDateHelp: 'الطلبة هيشوفوا الموعد والوقت المتبقي.',
      resultVisibility: 'إظهار النتيجة',
      visibilityImmediate: 'تظهر بعد التسليم مباشرة',
      visibilityAfterDeadline: 'تظهر بعد الموعد النهائي',
      visibilityAfterReview: 'تظهر بعد مراجعة المدرس أو المساعد',
      resultVisibilityHelp:
        'اختار وقت ظهور الدرجة والملاحظات للطالب في هذا الواجب.',
      gradingProfile: 'نوع التصحيح',
      gradingGeneral: 'مواد عامة',
      gradingArabicEssay: 'مقال عربي',
      gradingProfileHelp:
        'اختار منطق التصحيح المستخدم لهذا الواجب.',
    },
    createAssignmentErrors: {
      titleRequired: 'عنوان الواجب مطلوب.',
      totalPointsRequired: 'إجمالي الدرجات مطلوب.',
      totalPointsInvalid: 'اكتب قيمة صحيحة لإجمالي الدرجات أكبر من صفر.',
      dueDateRequired: 'موعد التسليم مطلوب عند اختيار إظهار النتيجة بعد الموعد النهائي.',
      modelAnswerRequired: 'من فضلك ارفع ملف الإجابة النموذجية PDF.',
      createFailed: 'فشل إنشاء الواجب. حاول مرة أخرى.',
    },
    createClassroom: {
      title: 'إنشاء فصل',
      name: 'اسم الفصل',
      namePlaceholder: 'مثال: فيزياء 101',
      nameHelp: 'خليه قصير وواضح للطلبة.',
    },
    gradeOnBehalf: {
      assignmentFallback: 'واجب',
      studentFallback: 'طالب',
      heroBody: 'ارفع وصحح واجبات الطلاب بالنيابة داخل هذا الفصل.',
      savedFeedbacks: 'ملاحظات محفوظة',
      submissionForm: 'نموذج التسليم',
      pdfOnly: 'ملفات PDF فقط',
      studentName: 'اسم الطالب',
      pdfFile: 'ملف PDF',
      submissionReceived: 'تم استلام التسليم',
      aiResult: 'نتيجة التصحيح بالذكاء الاصطناعي',
      processedSuccessfully: 'تمت معالجة التسليم بالنيابة بنجاح.',
      latestSubmissions: 'أحدث التسليمات بالنيابة',
      noSavedFeedbacks: 'لا توجد ملاحظات محفوظة حتى الآن.',
      notAvailable: 'غير متاح',
    },
    progress: {
      title: 'تقدّم الطالب',
      subtitle: 'التقدّم',
      headline: 'تابع تطوّرك',
      average: 'المتوسط',
      best: 'الأفضل',
      latest: 'الأحدث',
      count: 'عدد التسليمات',
      trend: 'منحنى الدرجات',
      trendHelp: 'درجاتك عبر الوقت حسب ملاحظات الذكاء الاصطناعي.',
      advice: 'نصايح',
      noData: 'مفيش بيانات لسه. سلّم واجبات عشان نشوف التقدّم.',
      excellent: 'شغل ممتاز. كمل بنفس المستوى وراجع إجاباتك بدقة.',
      good: 'تقدّم كويس. ركّز على نقاط الضعف عشان تعلى أكتر.',
      needsWork: 'درجاتك منخفضة. راجع الملاحظات واطلب مساعدة لو محتاج.',
      keepGoing: 'كمّل. سلّم واجبات أكتر عشان نبني الرسم البياني.',
    },
    subscription: {
      trial: 'تجربة',
      active: 'نشط',
      pastDue: 'متأخر',
      canceled: 'ملغي',
      expired: 'الاشتراك منتهي',
      inactive: 'الاشتراك غير نشط',
      manage: 'إدارة الاشتراكات',
      trialCta: 'أنت على تجربة. اشترك عشان تكمل بعد انتهائها.',
      inactiveCta: 'اشتراكك غير نشط. جدّد للاستمرار.',
      activeCta: 'اشتراكك نشط.',
    },
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem('lang') || 'en');

  useEffect(() => {
    localStorage.setItem('lang', lang);
    document.documentElement.lang = lang === 'ar' ? 'ar' : 'en';
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const t = useMemo(() => {
    const dict = translations[lang] || translations.en;
    return (key) => {
      const parts = key.split('.');
      let value = dict;
      for (const part of parts) {
        value = value?.[part];
      }
      return value || key;
    };
  }, [lang]);

  const toggleLang = () => {
    setLang((prev) => (prev === 'ar' ? 'en' : 'ar'));
  };

  return (
    <I18nContext.Provider value={{ lang, t, toggleLang }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
};
